/**
 * Subscription Tier Configuration
 *
 * Defines the feature matrix for each Forge subscription tier.
 * Tier gates set the ceiling; org admins can toggle OFF features
 * their tier includes, but cannot enable features above their tier.
 *
 * Tiers (ascending): free → plant → harvest → flourish
 */

// ── Tier Definitions ─────────────────────────────────────────

export const TIER_ORDER = ["free", "plant", "harvest", "flourish"] as const;
export type SubscriptionTier = (typeof TIER_ORDER)[number];

/** Numeric rank for easy comparison (higher = more features) */
const TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  plant: 1,
  harvest: 2,
  flourish: 3,
};

export function tierRank(tier: SubscriptionTier): number {
  return TIER_RANK[tier] ?? 0;
}

export function tierMeetsMinimum(
  currentTier: SubscriptionTier,
  requiredTier: SubscriptionTier,
): boolean {
  return tierRank(currentTier) >= tierRank(requiredTier);
}

// ── Module Definitions ───────────────────────────────────────

/**
 * Toggleable modules that can be enabled/disabled per organization.
 * Each module maps to sidebar sections, route groups, and Quick Add items.
 */
export const TOGGLEABLE_MODULES = [
  "kids_checkin",
  "volunteer_scheduling",
  "prayer_wall",
  "online_giving",
  "member_directory",
  "groups",
  "projects_tasks",
  "asset_management",
  "hr_staff",
  "accounting",
  "pledge_management",
  "sms_messaging",
  "engagement_dashboards",
] as const;

export type ToggleableModule = (typeof TOGGLEABLE_MODULES)[number];

// ── Feature Matrix ───────────────────────────────────────────

export interface ModuleConfig {
  /** Human-readable display name */
  label: string;
  /** Short description for the settings UI */
  description: string;
  /** Minimum tier required to enable this module */
  minimumTier: SubscriptionTier;
  /** Whether the module is ON by default when the tier qualifies */
  defaultEnabled: boolean;
  /** Category grouping for the settings UI */
  category: "ministry" | "operations" | "finance" | "engagement" | "communications";
}

export const MODULE_CONFIG: Record<ToggleableModule, ModuleConfig> = {
  kids_checkin: {
    label: "Kids Check-In",
    description: "Manage child check-in/check-out for services and events",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "ministry",
  },
  volunteer_scheduling: {
    label: "Volunteer Scheduling",
    description: "Schedule and manage volunteer teams for services and events",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "ministry",
  },
  prayer_wall: {
    label: "Prayer Wall",
    description: "Public prayer request wall on the Member Portal",
    minimumTier: "plant",
    defaultEnabled: true,
    category: "engagement",
  },
  online_giving: {
    label: "Online Giving",
    description: "Accept contributions through the Member Portal",
    minimumTier: "plant",
    defaultEnabled: true,
    category: "finance",
  },
  member_directory: {
    label: "Member Directory",
    description: "Searchable member directory on the Member Portal",
    minimumTier: "plant",
    defaultEnabled: true,
    category: "engagement",
  },
  groups: {
    label: "Groups & Small Groups",
    description: "Manage small groups, Bible studies, and recurring gatherings",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "engagement",
  },
  projects_tasks: {
    label: "Projects & Tasks",
    description: "Project management with task tracking and assignments",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "operations",
  },
  asset_management: {
    label: "Asset Management",
    description: "Track physical resources, rooms, and equipment",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "operations",
  },
  hr_staff: {
    label: "HR & Staff Management",
    description: "Staff directory, recruitment, onboarding, and HR tracking",
    minimumTier: "flourish",
    defaultEnabled: true,
    category: "operations",
  },
  accounting: {
    label: "Accounting Module",
    description: "Full GAAP/ASC 958 accounting with GL, AP/AR, and financial statements",
    minimumTier: "flourish",
    defaultEnabled: true,
    category: "finance",
  },
  pledge_management: {
    label: "Pledge Management",
    description: "Create and track pledge campaigns with member commitments",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "finance",
  },
  sms_messaging: {
    label: "SMS Messaging",
    description: "Send SMS messages to members and groups",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "communications",
  },
  engagement_dashboards: {
    label: "Engagement Dashboards",
    description: "Visual dashboards showing member engagement patterns and trends",
    minimumTier: "harvest",
    defaultEnabled: true,
    category: "engagement",
  },
};

// ── Standalone Feature Gates (non-module) ────────────────────

/**
 * Features that aren't full modules but still need tier gating.
 * These don't appear in module toggles — they're always-on for qualifying tiers.
 */
export interface FeatureGateConfig {
  label: string;
  description: string;
  minimumTier: SubscriptionTier;
}

export const FEATURE_GATES: Record<string, FeatureGateConfig> = {
  solomon_ai: {
    label: "Ask Solomon AI",
    description: "Natural language queries, insights, and task assistance",
    minimumTier: "flourish",
  },
  custom_reports: {
    label: "Custom Report Builder",
    description: "Build custom reports with drag-and-drop fields",
    minimumTier: "harvest",
  },
  api_access: {
    label: "API Access",
    description: "REST API access for custom integrations",
    minimumTier: "flourish",
  },
  multi_campus: {
    label: "Multi-Campus",
    description: "Manage multiple campuses under one organization",
    minimumTier: "flourish",
  },
  advanced_analytics: {
    label: "Advanced Analytics",
    description: "Trend reporting, predictive insights, and advanced data analysis",
    minimumTier: "flourish",
  },
  bulk_operations: {
    label: "Bulk Import/Export",
    description: "Bulk data import and export across all modules",
    minimumTier: "harvest",
  },
  white_label_portal: {
    label: "White-Label Member Portal",
    description: "Custom branding and domain for the Member Portal",
    minimumTier: "flourish",
  },
  care_workflows: {
    label: "Care Request Workflows",
    description: "Auto-assignment, follow-up, and escalation for care requests",
    minimumTier: "harvest",
  },
  approval_workflows: {
    label: "Finance Approval Workflows",
    description: "Require approval for journal entries, payments, and expense reports",
    minimumTier: "flourish",
  },
  audit_log_access: {
    label: "Audit Log Access",
    description: "View detailed audit trail of all system changes",
    minimumTier: "flourish",
  },
};

// ── Tier Display Info ────────────────────────────────────────

export interface TierDisplayInfo {
  name: string;
  tagline: string;
  monthlyPrice: number;
  yearlyPrice: number;
  /** Badge color class (Tailwind) */
  badgeColor: string;
  /** Icon emoji */
  icon: string;
}

export const TIER_DISPLAY: Record<SubscriptionTier, TierDisplayInfo> = {
  free: {
    name: "Free Trial",
    tagline: "Try Forge risk-free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    badgeColor: "bg-muted text-muted-foreground",
    icon: "🆓",
  },
  plant: {
    name: "Plant",
    tagline: "Get Growing",
    monthlyPrice: 59,
    yearlyPrice: 590,
    badgeColor: "bg-blue-100 text-blue-700 dark:bg-info-700/30 dark:text-info-700",
    icon: "🌱",
  },
  harvest: {
    name: "Harvest",
    tagline: "The Full Toolkit",
    monthlyPrice: 149,
    yearlyPrice: 1490,
    badgeColor: "bg-amber-100 text-amber-700 dark:bg-warning-700/30 dark:text-warning-700",
    icon: "🌾",
  },
  flourish: {
    name: "Flourish",
    tagline: "Enterprise Without the Enterprise Price",
    monthlyPrice: 299,
    yearlyPrice: 2990,
    badgeColor: "bg-accent-purple-100 text-accent-purple-700 dark:bg-accent-purple-700/30 dark:text-accent-purple-700",
    icon: "🌻",
  },
};

// ── Module → Route Mapping ───────────────────────────────────

/**
 * Maps toggleable modules to their route prefixes and RBAC module names.
 * Used by the sidebar to hide gated items and by TierRouteGate for redirects.
 */
export interface ModuleRouteMapping {
  /** Route path prefixes that belong to this module */
  routePrefixes: string[];
  /** Sidebar nav item labels that belong to this module (for label-based filtering) */
  sidebarLabels?: string[];
}

export const MODULE_ROUTE_MAP: Record<ToggleableModule, ModuleRouteMapping> = {
  kids_checkin: {
    routePrefixes: ["/kids-check-in"],
  },
  volunteer_scheduling: {
    routePrefixes: ["/volunteers", "/volunteer"],
  },
  prayer_wall: {
    routePrefixes: [], // Member Portal only — no staff routes
  },
  online_giving: {
    routePrefixes: [], // Embedded in Finance — gated at component level
  },
  member_directory: {
    routePrefixes: [], // Member Portal only
  },
  groups: {
    routePrefixes: ["/groups"],
  },
  projects_tasks: {
    routePrefixes: ["/projects"],
  },
  asset_management: {
    routePrefixes: ["/assets"],
  },
  hr_staff: {
    routePrefixes: ["/hr"],
    sidebarLabels: ["HR"],
  },
  accounting: {
    routePrefixes: ["/finance/accounting", "/finance/chart-of-accounts", "/finance/general-ledger", "/finance/journal-entries", "/finance/ap", "/finance/ar", "/finance/banking", "/finance/budgets"],
  },
  pledge_management: {
    routePrefixes: ["/pledges", "/finance/pledge-discounting", "/finance/pledge-reminders"],
  },
  sms_messaging: {
    routePrefixes: ["/communications/sms-usage"],
  },
  engagement_dashboards: {
    routePrefixes: ["/engagement"],
  },
};

// ── Workflow Settings ────────────────────────────────────────

/**
 * Workflow settings are more granular toggles within enabled modules.
 * These control automation behaviors, not module visibility.
 */
export const WORKFLOW_SETTINGS = [
  "care_auto_assignment",
  "contribution_auto_receipting",
  "attendance_followup_alerts",
  "new_visitor_auto_workflow",
  "volunteer_reminder_notifications",
  "pledge_reminder_emails",
  "solomon_ai_suggestions",
  "multi_channel_send",
  "approval_workflows_finance",
  "background_check_integration",
] as const;

export type WorkflowSetting = (typeof WORKFLOW_SETTINGS)[number];

export interface WorkflowSettingConfig {
  label: string;
  description: string;
  minimumTier: SubscriptionTier;
  defaultEnabled: boolean;
  /** Which toggleable module this workflow belongs to (if any) */
  parentModule?: ToggleableModule;
  category: "care" | "finance" | "engagement" | "volunteers" | "communications" | "ai";
}

export const WORKFLOW_CONFIG: Record<WorkflowSetting, WorkflowSettingConfig> = {
  care_auto_assignment: {
    label: "Care Request Auto-Assignment",
    description: "Auto-assign care requests by category to designated staff members",
    minimumTier: "harvest",
    defaultEnabled: false,
    category: "care",
  },
  contribution_auto_receipting: {
    label: "Contribution Auto-Receipting",
    description: "Automatically email receipts when contributions are recorded",
    minimumTier: "plant",
    defaultEnabled: false,
    category: "finance",
  },
  attendance_followup_alerts: {
    label: "Attendance Follow-Up Alerts",
    description: "Notify staff when a family hasn't attended in a configurable number of weeks",
    minimumTier: "harvest",
    defaultEnabled: false,
    category: "engagement",
  },
  new_visitor_auto_workflow: {
    label: "New Visitor Auto-Workflow",
    description: "Trigger a task sequence when a new visitor is added to the system",
    minimumTier: "harvest",
    defaultEnabled: false,
    category: "engagement",
  },
  volunteer_reminder_notifications: {
    label: "Volunteer Reminder Notifications",
    description: "Auto-send schedule reminders to volunteers before their assigned services",
    minimumTier: "harvest",
    // OFF by default (W6-5 / D6): new orgs opt IN to automated outbound reminders
    // rather than having them silently fire from day one.
    defaultEnabled: false,
    parentModule: "volunteer_scheduling",
    category: "volunteers",
  },
  pledge_reminder_emails: {
    label: "Pledge Reminder Emails",
    description: "Auto-send pledge progress and reminder emails to donors",
    minimumTier: "harvest",
    defaultEnabled: false,
    parentModule: "pledge_management",
    category: "finance",
  },
  solomon_ai_suggestions: {
    label: "Solomon AI Suggestions",
    description: "Proactive AI-generated insights, nudges, and task suggestions",
    minimumTier: "flourish",
    // OFF by default (W6-5 / D6): proactive AI suggestions are opt-in, not opt-out.
    defaultEnabled: false,
    category: "ai",
  },
  multi_channel_send: {
    label: "Multi-Channel Send",
    description: "Send messages via email + SMS simultaneously from a single compose",
    minimumTier: "harvest",
    defaultEnabled: false,
    parentModule: "sms_messaging",
    category: "communications",
  },
  approval_workflows_finance: {
    label: "Finance Approval Workflows",
    description: "Require approval for journal entries, payments, and expense reports",
    minimumTier: "flourish",
    defaultEnabled: false,
    parentModule: "accounting",
    category: "finance",
  },
  background_check_integration: {
    label: "Background Check Integration",
    description: "Enable MinistrySafe or similar integration for volunteer screening",
    minimumTier: "harvest",
    defaultEnabled: false,
    parentModule: "volunteer_scheduling",
    category: "volunteers",
  },
};

export function getDefaultWorkflowSettings(
  tier: SubscriptionTier,
): Record<WorkflowSetting, boolean> {
  const settings = {} as Record<WorkflowSetting, boolean>;
  for (const key of WORKFLOW_SETTINGS) {
    const config = WORKFLOW_CONFIG[key];
    settings[key] = tierMeetsMinimum(tier, config.minimumTier) && config.defaultEnabled;
  }
  return settings;
}

// ── Helpers ──────────────────────────────────────────────────

/** Build the default module toggles for a given tier */
export function getDefaultModuleToggles(
  tier: SubscriptionTier,
): Record<ToggleableModule, boolean> {
  const toggles = {} as Record<ToggleableModule, boolean>;
  for (const mod of TOGGLEABLE_MODULES) {
    const config = MODULE_CONFIG[mod];
    // Only enable if the tier qualifies AND the default is on
    toggles[mod] = tierMeetsMinimum(tier, config.minimumTier) && config.defaultEnabled;
  }
  return toggles;
}

/** Get all modules available at a given tier (regardless of toggle state) */
export function getAvailableModules(tier: SubscriptionTier): ToggleableModule[] {
  return TOGGLEABLE_MODULES.filter((mod) =>
    tierMeetsMinimum(tier, MODULE_CONFIG[mod].minimumTier),
  );
}

/** Get all features available at a given tier */
export function getAvailableFeatures(tier: SubscriptionTier): string[] {
  return Object.entries(FEATURE_GATES)
    .filter(([_, config]) => tierMeetsMinimum(tier, config.minimumTier))
    .map(([key]) => key);
}
